import React, {useCallback, useEffect, useRef, useState} from 'react';
import Modal from 'react-modal';
import {useSelector} from 'react-redux';
import cn from 'classnames';

import {useLockScroll} from 'coreHooks';
import {selectPopupIndex} from 'coreStore/data/ui/index';
import {RootState} from 'coreStore/types';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {EPopupDirections, IPopupProps} from './IPopup';
import {PopupCloseButton} from './Subcomponents';

import styles from './Popup.module.scss';

const Popup = ({
	children,
	id,
	direction = EPopupDirections.POPUP_DIRECTION_BOTTOM_TO_TOP,
	onClose = null,
	className,
	overlayClassName,
	popupContentClassName,
	showCloseButton = true,
	closeButtonClassName,
	closeButtonLabel,
	isSoftScroll,
	onAfterOpen = null,
	onAfterClose = null,
	restoreScrollPosition = true,
	style,
	shouldCloseOnOverlayClick = true,
}: IPopupProps) => {

	const {managerRepository} = useGeneralProvider();
	const [isOpen, setIsOpen] = useState(false);
	const firstOpen = useRef(false);
	const popupIndex = useSelector((state: RootState) => selectPopupIndex(state, id));
	const isPopupOpen = popupIndex !== -1;
	const {lockScroll} = useLockScroll({
		restoreScrollPosition,
		lockerClassName: isSoftScroll ? 'soft-scroll' : 'lock-scroll',
	});

	const closePopup = useCallback(() => {
		managerRepository.getPopupsMgr().closePopup(id);

		if (typeof onClose === 'function') {
			onClose();
		}
	}, []);

	useEffect(() => {
		if (isPopupOpen) {
			lockScroll(true);
		} else if (firstOpen.current) {
			lockScroll(false);
		}

		return () => {
			if (isPopupOpen) {
				lockScroll(false);
			}
		};
	}, [isPopupOpen]);

	const conditionalProps: Record<string, any> = {};

	if (style) {
		conditionalProps.style = style;
	}

	return (
		<Modal
			onRequestClose={() => closePopup()}
			shouldCloseOnEsc={true}
			isOpen={isPopupOpen}
			closeTimeoutMS={750}
			portalClassName={cn(className, styles['popup'], 'popup', {
				opened: isOpen,
				[styles[`popup_direction-${direction}`]]: direction,
				[className]: className,
			})}
			overlayClassName={cn(styles['popup__overlay'], 'popup__overlay', overlayClassName)}
			bodyOpenClassName={null}
			htmlOpenClassName={null}
			overlayRef={(node) => {
				if (node && node.parentElement) {
					node.parentElement.style.zIndex = (parseInt(popupIndex.toString(), 10) + 170).toFixed();
				}
			}}
			className={cn(styles['popup__content'], popupContentClassName, 'popup__content')}
			onAfterOpen={() => {
				onAfterOpen && onAfterOpen();
				firstOpen.current = true;
			}}
			onAfterClose={() => {
				onAfterClose && onAfterClose();
				setIsOpen(false);
			}}
			parentSelector={() => document.getElementById('app-main')}
			{...conditionalProps}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
		>
			{showCloseButton && (
				<PopupCloseButton
					id={id}
					closePopup={closePopup}
					closeButtonDimension={{width: 20, height: 20}}
					closeButtonLabel={closeButtonLabel}
					closeButtonClassName={closeButtonClassName}
				/>
			)}
			{children}
		</Modal>
	);
};

export default Popup;
